import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toPersianDigits } from '@bookora/shared';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  IconButton,
  Icon,
  Popover,
  SkeletonBlock,
  SkeletonGroup,
  Typography,
} from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import {
  fetchUnreadCount,
  listNotifications,
  markNotificationRead,
} from '@/api/endpoints/notifications';
import { queryKeys } from '@/api/query-keys';
import type { StaffNotification } from '@/api/types';
import { useAuth } from '@/auth/token-store';
import { tourAnchor } from '@/guidance';
import { isPanelPath } from '@/lib/panel-path';
import { toast } from '@/lib/feedback';

import { groupNotifications, type NotificationGroup } from './notification-groups';
import { NotificationGroupRow, NotificationRow } from './notification-row';
import { NotificationsDrawer } from './notifications-drawer';
import { MARK_ALL_READ_LABEL, useMarkAllNotificationsRead } from './use-mark-all-read';

/**
 * The bell, its unread count, and the last few notifications.
 *
 * The count polls every 60s. That is deliberately unambitious: the alternative
 * is a websocket for one integer, on an Iranian connection, in a panel that
 * already has a tab open all day. A minute is well inside the useful window for
 * «درخواست نوبت جدید» and costs one tiny request.
 *
 * The list itself is only fetched when the popover opens — there is no reason to
 * pull twenty rows for a badge.
 *
 * The server filters by permission before returning anything, so a receptionist
 * never sees a low-stock alert whose numbers she has no access to.
 *
 * ── WHY THE POPOVER IS DRIVEN BY HAND ───────────────────────────────────────
 *
 * It is a dialog-shaped panel, and `@rc-component/trigger` gives it none of a
 * dialog's keyboard behaviour: no keydown handler at all, so **Escape did not
 * close it**; no focus move on open; and the content is portalled to the END of
 * `<body>`, so Tab from the bell walked the rest of the header rather than the
 * list. The rows were `<List.Item onClick>` — no tab stop, no role, no key
 * handler. A keyboard user could open the panel and then do nothing in it.
 *
 * So `open` is controlled here and the four things AntD does not do are done
 * explicitly: focus moves to the panel on open, Tab cycles WITHIN it (the
 * portal position makes an untrapped Tab meaningless), Escape closes it and
 * hands focus back to the bell, and every row is a real `<button>`.
 *
 * It is deliberately NOT `aria-modal`: the page behind stays visible and usable
 * with a pointer, and claiming modality we do not enforce is the same class of
 * lie as a `role="tablist"` with no tabs.
 *
 * ── THE PREVIEW IS NOT THE WHOLE STORY ──────────────────────────────────────
 *
 * The popover shows the latest eight. After a busy hour, a booking request
 * from this morning sat ninth, under eight stock alerts, and there was no way
 * to reach it. «همهٔ اعلان‌ها» at the foot opens `NotificationsDrawer`: every
 * notification, paged, filterable to the unread ones.
 *
 * Stock notices of one type are folded into a single row in this preview
 * (`notification-groups.ts`), so a dozen expiring lots cannot push the one
 * booking request out of it.
 *
 * ── THE BELL KEEPS THE DASHBOARD HONEST ─────────────────────────────────────
 *
 * When the unread count goes UP, something happened — usually a booking — so
 * the dashboard's agenda and booking-request tile are re-read at once rather
 * than on their next minute tick. The bell announcing a new booking while
 * «نوبت‌های امروز» still showed the morning was exactly the mismatch staff
 * noticed.
 *
 * ── …AND THE BADGE DOES NOT WAIT A MINUTE FOR THE PANEL'S OWN ACTIONS ───────
 *
 * The count used to move only on its 60-second poll. Cancelling a visit files
 * a «نوبت لغو شد» notice for the desk a moment later, and the badge sat on its
 * old number while the popover already listed the new row — the bell
 * contradicting itself. The count is now re-read shortly after any successful
 * write in the panel (the notice is filed by a worker, so not synchronously),
 * and whenever the popover opens, so the badge and the list it heads agree.
 */

/** How long after a successful write the badge is re-read. */
const AFTER_WRITE_MS = 2_000;

/** Everything the trap counts as a stop. The panel itself is `tabindex="-1"`. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const POLL_MS = 60_000;
const PREVIEW_SIZE = 8;

export function NotificationBell() {
  const { activeClinicId } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  /*
   * Whether this opening of the panel had to show the row skeleton. Only then
   * does the list (or its empty/error state) fade in: the popover already has
   * its own entrance, and fading cached rows inside it on every opening would
   * only make them arrive after the frame around them.
   */
  const showedSkeleton = useRef(false);

  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount(activeClinicId),
    queryFn: ({ signal }) => fetchUnreadCount(signal),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    enabled: Boolean(activeClinicId),
  });

  useEffect(() => {
    let timer: number | undefined;
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.unreadCount(activeClinicId),
        });
      }, AFTER_WRITE_MS);
    });
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [queryClient, activeClinicId]);

  const total = unread.data?.total ?? 0;
  const previousTotal = useRef<number | null>(null);
  useEffect(() => {
    if (!unread.data) return;
    const before = previousTotal.current;
    previousTotal.current = unread.data.total;
    if (before !== null && unread.data.total > before) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all(activeClinicId) });
    }
  }, [unread.data, queryClient, activeClinicId]);

  const list = useQuery({
    queryKey: queryKeys.notifications.list(activeClinicId, 1, PREVIEW_SIZE),
    queryFn: ({ signal }) => listNotifications({ page: 1, pageSize: PREVIEW_SIZE }, signal),
    enabled: false,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(activeClinicId) });
    },
    onError: (error) => toast.error(error),
  });

  const markAll = useMarkAllNotificationsRead();

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) bellRef.current?.focus();
  }, []);

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (next) {
      showedSkeleton.current = false;
      void list.refetch();
      void unread.refetch();
    }
    // A click outside, or on the bell itself, must not yank focus back to the
    // bell — only an explicit dismissal does.
    else if (panelRef.current?.contains(document.activeElement)) bellRef.current?.focus();
  }

  /**
   * The panel's own keyboard contract: Escape dismisses, Tab cycles inside.
   *
   * The cycle is not gold-plating. The content is portalled to the end of
   * `<body>`, so an untrapped Tab off the last row lands in browser chrome and
   * a Shift+Tab off the first lands at the bottom of the page — nowhere near
   * the bell the user opened it from.
   */
  function handlePanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = panelRef.current;
    if (!panel) return;
    const stops = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (stops.length === 0) {
      event.preventDefault();
      return;
    }

    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  /**
   * Initial focus, as a callback ref rather than an effect.
   *
   * The overlay is mounted by the portal on its own schedule; a `useEffect` on
   * `open` fires before the content node exists and focuses nothing. The ref
   * fires exactly when the node lands.
   *
   * The focus itself is deferred one frame. Opening is a CLICK — Enter on the
   * bell synthesises one — and the click completes after the React commit that
   * mounted this node; focusing synchronously here means the click's own focus
   * handling lands last and puts focus straight back on the bell. One frame
   * later there is nothing left to fight with, and it is also when the
   * overlay's enter motion has settled.
   */
  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    if (!node) return;
    requestAnimationFrame(() => {
      if (panelRef.current === node && !node.contains(document.activeElement)) node.focus();
    });
  }, []);

  function handleClick(item: StaffNotification): void {
    if (!item.isRead) markRead.mutate(item.id);
    // `link` is a panel route the server computed. It is only followed when it
    // is a relative path — a notification must never be able to navigate the
    // panel to another origin.
    if (isPanelPath(item.link)) void navigate(item.link);
    // Following a row dismisses the panel — leaving it open over the page it
    // just navigated to would hide the result. Focus goes back to the bell,
    // which is still in the header of wherever we landed; dropping it on
    // `<body>` would restart the keyboard user at the top of the document.
    if (item.link) close();
  }

  function handleGroupClick(group: NotificationGroup): void {
    for (const item of group.items) if (!item.isRead) markRead.mutate(item.id);
    void navigate(group.link);
    close();
  }

  const listLoading = list.isFetching && !list.data;
  if (listLoading) showedSkeleton.current = true;
  const settleClass = showedSkeleton.current ? 'bk-feedback-enter' : undefined;

  return (
    <>
      <Popover
        trigger="click"
        // `bottom-end`: this trigger is at the inline END of the header, so
        // the panel must hang from that edge or it runs off the viewport.
        // Same pixels as the old physical `bottomLeft` in RTL, and the
        // right answer rather than a coincidence.
        placement="bottom-end"
        arrow={false}
        open={open}
        onOpenChange={handleOpenChange}
        // Unmounted when closed, so a dismissed panel is genuinely gone from the
        // accessibility tree rather than merely hidden behind a transform.
        destroyOnHidden
        content={
          <div
            ref={attachPanel}
            role="dialog"
            aria-label="اعلان‌ها"
            tabIndex={-1}
            onKeyDown={handlePanelKeyDown}
            className="bk-notification-panel"
          >
            <div className="bk-notification-panel__head">
              <Typography.Text strong>اعلان‌ها</Typography.Text>
              {/* Only while something is unread: with nothing to mark it was
                  a greyed button with no reason. In flight is `loading`. */}
              {total > 0 ? (
                <Button
                  type="link"
                  size="small"
                  icon={<Icon name="check" />}
                  loading={markAll.isPending}
                  onClick={() => markAll.mutate()}
                >
                  {MARK_ALL_READ_LABEL}
                </Button>
              ) : null}
            </div>

            {listLoading ? (
              // Row-shaped placeholders, not a lone spinner: the popover keeps
              // its size and the list lands where the skeleton was.
              <SkeletonGroup label="در حال دریافت اعلان‌ها…" className="bk-notification-skeleton">
                {[0, 1, 2].map((row) => (
                  <div key={row} style={{ display: 'grid', gap: 'var(--bk-space-1-5)' }}>
                    <SkeletonBlock shape="text" width={row === 1 ? '55%' : '70%'} />
                    <SkeletonBlock shape="text" width="90%" />
                    <SkeletonBlock shape="text" width="30%" />
                  </div>
                ))}
              </SkeletonGroup>
            ) : list.error ? (
              <div className={settleClass}>
                <ErrorState error={list.error} onRetry={() => void list.refetch()} compact />
              </div>
            ) : (list.data?.items.length ?? 0) === 0 ? (
              <div className={settleClass}>
                <EmptyState
                  compact
                  title="اعلان جدیدی ندارید"
                  description="درخواست‌های نوبت، هشدار موجودی و پیام‌های سامانه رو اینجا می‌بینید."
                />
              </div>
            ) : (
              // A plain list rather than AntD's `List`: each row is already a
              // button with its own layout, and the list's item chrome (borders,
              // padding, an `<h4>` meta) had to be undone on every row.
              <ul
                className={['bk-notification-panel__list', settleClass].filter(Boolean).join(' ')}
              >
                {groupNotifications(list.data?.items ?? []).map((entry) =>
                  entry.kind === 'single' ? (
                    <li key={entry.item.id}>
                      <NotificationRow item={entry.item} onOpen={handleClick} />
                    </li>
                  ) : (
                    <li key={`group-${entry.group.type}`}>
                      <NotificationGroupRow group={entry.group} onOpen={handleGroupClick} />
                    </li>
                  ),
                )}
              </ul>
            )}

            <div className="bk-notification-panel__foot">
              <Button
                type="link"
                size="small"
                onClick={() => {
                  close(false);
                  setDrawerOpen(true);
                }}
              >
                همهٔ اعلان‌ها
                <Icon name="chevron-forward" size={16} />
              </Button>
            </div>
          </div>
        }
      >
        {/*
        AntD renders `count` verbatim, so a number here prints Latin digits in
        an all-Persian header — beside an `aria-label` that is already Persian,
        so the screen and the screen reader disagreed. The overflow cap is
        applied here rather than through `overflowCount`, which only understands
        a numeric count. `0` stays a number so the badge hides itself.
      */}
        {/* Default size, not `small`: the small badge clipped «۲۷» over the
          glyph and was hard to read at a glance across a reception desk. */}
        <Badge
          count={total === 0 ? 0 : toPersianDigits(total > 99 ? '99+' : total)}
          className="bk-bell-badge"
        >
          {/* `IconButton`, like the menu toggle and the theme toggle beside it,
            so the three header controls share one shape and one glyph size. */}
          <IconButton
            ref={bellRef}
            {...tourAnchor('shell.bell')}
            type="text"
            name="bell"
            // The bell opens its own panel at once; a label bubble would only
            // flash over it. Its popup ARIA is hand-set here because the
            // Popover's child is the Badge, not this button.
            tooltip={false}
            aria-haspopup="dialog"
            aria-expanded={open}
            label={total > 0 ? `اعلان‌ها، ${toPersianDigits(total)} خوانده‌نشده` : 'اعلان‌ها'}
            // Escape works from the bell too: the panel opens with focus inside
            // it, but a pointer user who then Shift+Tabs back out still expects
            // Escape to dismiss.
            onKeyDown={(event) => {
              if (event.key === 'Escape' && open) {
                event.preventDefault();
                close();
              }
            }}
          />
        </Badge>
      </Popover>
      <NotificationsDrawer
        open={drawerOpen}
        unreadTotal={total}
        onClose={() => {
          setDrawerOpen(false);
          bellRef.current?.focus();
        }}
        onOpenItem={(item) => {
          handleClick(item);
          if (isPanelPath(item.link)) setDrawerOpen(false);
        }}
      />
    </>
  );
}
